import { OrderLineTypes, PaymentStatus } from "@repo/database";
import { formatIntToCurrency } from "@repo/utils";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useShallow } from "zustand/react/shallow";

import type { CartItem } from "~/providers/cart-provider";
import type { Order } from "~/providers/order-provider";
import type { Table } from "~/providers/table-provider";
import { useCartTable } from "~/hooks/use-cart-table";
import { useDragScroll } from "~/hooks/use-drag-scroll";
import { useLabel } from "~/hooks/use-label";
import { cn } from "~/lib/utils";
import { useOrderManager } from "~/managers/use-order-manager";
import { useCartStore } from "~/stores/use-cart-store";
import { useOrderStore } from "~/stores/use-order-store";
import { useRegisterStore } from "~/stores/use-register-store";
import { Button } from "../ui/button";
import ItemsTable from "./items-table";

function CartTotal() {
  const { items } = useCartStore();

  const { currentOrder } = useOrderStore();

  const { initializeCheckout } = useOrderManager();

  const itemsTotal = items
    .filter((item) => item.type !== OrderLineTypes.discount)
    .reduce((acc, item) => acc + item.value * item.quantity, 0);

  const discountTotal = items
    .filter((item) => item.type === OrderLineTypes.discount)
    .reduce((acc, item) => acc + item.value * item.quantity, 0);

  const paymentsTotal =
    currentOrder?.payments
      .filter((p) => p.paymentStatus === PaymentStatus.paid)
      .reduce((acc, curr) => acc + curr.amount, 0) ?? 0;

  const total = itemsTotal - discountTotal;

  const tolerance = 0.01;

  const amountDue = Math.max(total - paymentsTotal, 0);

  const displayAmountDue = amountDue <= tolerance ? 0 : amountDue;

  return (
    <div className="border-t-2">
      <div className="flex flex-row items-center justify-end h-14 gap-1 dark:bg-background-tertiary bg-white p-1 font-medium dark:text-white text-black">
        <button
          className={cn(
            paymentsTotal > 0 && "gap-2 flex flex-row items-center",
          )}
          onClick={initializeCheckout}
        >
          <span className={cn(paymentsTotal > 0 ? "text-md" : "text-2xl")}>
            {formatIntToCurrency(total)}
          </span>
          {paymentsTotal > 0 && (
            <span
              className={cn(
                displayAmountDue > 0 ? "text-red-500" : "text-green-500",
                "text-2xl",
              )}
            >
              {formatIntToCurrency(displayAmountDue)}
            </span>
          )}
        </button>
      </div>
    </div>
  );
}

export function CashRegisterCart({
  orders,
  table,
}: {
  orders?: Order[];
  table?: Table;
}) {
  const { t } = useTranslation("common");

  const navigate = useNavigate();

  const { labels } = useLabel();

  const { currentOrder } = useOrderStore(
    useShallow((state) => ({
      currentOrder: state.currentOrder,
    })),
  );

  const { setOrderSettingsOpen } = useRegisterStore();

  const { setSelectedItem, setCartItemContextOpen, items } = useCartStore(
    useShallow((state) => ({
      setSelectedItem: state.setSelectedItem,
      setCartItemContextOpen: state.setCartItemContextOpen,
      items: state.items,
    })),
  );

  const { columns } = useCartTable({
    canSelect: false,
    items,
  });

  const [ref] = useDragScroll();

  const handleSelectItem = (item: CartItem) => {
    setSelectedItem(item);
    setCartItemContextOpen(true);
  };

  return (
    <div className="flex h-full flex-col justify-between @container">
      <div className="border-b-2 dark:bg-background-tertiary bg-white">
        <div className="flex flex-row justify-between gap-2 h-14">
          <div className="flex flex-row gap-1 flex-1">
            <button
              onClick={() => {
                setOrderSettingsOpen(true);
              }}
              className="flex flex-col border-r p-1 justify-center items-center min-w-[120px]"
            >
              <span className="text-sm italic">
                {table
                  ? currentOrder?.name
                    ? t("Cart.table_reference_name", {
                        reference: table.reference,
                        name: currentOrder.name,
                      })
                    : t("Cart.table_reference", {
                        reference: table.reference,
                      })
                  : t("Cart.no_table_selected")}
              </span>
              <span className="text-sm italic">
                {currentOrder?.labelIds
                  .map(
                    (labelId) =>
                      labels.find((label) => label.id === labelId)?.text,
                  )
                  .join(", ")}
              </span>
            </button>

            <button
              onClick={() => {
                setOrderSettingsOpen(true);
              }}
              className="p-1 flex items-center"
            >
              {currentOrder?.partySize && (
                <>
                  <span className="text-sm">
                    {t("Cart.party_size", { size: currentOrder.partySize })}
                  </span>
                </>
              )}
            </button>
          </div>

          {/* <div className="items-center flex flex-row gap-1 p-1 border-l">
            <Button
              onClick={handleCollapseMobileProducts}
              className={cn(
                "h-8 w-8 p-0 transition-all @xs:hidden",
                mobileProductsCollapsed && "rotate-180 !block",
              )}
              variant="ghost"
            >
              <ArrowRight />
            </Button>
          </div> */}
        </div>
      </div>

      {orders && orders.length > 1 && (
        <div
          ref={ref}
          className="p-1 border-b flex flex-row gap-2 overflow-auto hide-scrollbar"
        >
          {orders
            .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
            .map((order) => (
              <Button
                onClick={() => {
                  void navigate({
                    to: "/cash-register",
                    search: {
                      orderId: order.id,
                      tableId: order.tableId,
                    },
                  });
                }}
                className="flex flex-col gap-1 text-left items-start h-auto w-44"
                key={order.id}
                variant={order.id === currentOrder?.id ? "primary" : "outline"}
              >
                <span>
                  {t("Order.total", {
                    total: formatIntToCurrency(order.total),
                  })}
                </span>
                <span>
                  {t("Order.paid", {
                    paid: formatIntToCurrency(order.amountPaid),
                  })}
                </span>
                <span>
                  {t("Order.outstanding", {
                    outstanding: formatIntToCurrency(
                      order.total - order.amountPaid,
                    ),
                  })}
                </span>
              </Button>
            ))}
        </div>
      )}

      <ItemsTable
        items={items.sort(
          (a, b) => a.createdAt.getTime() - b.createdAt.getTime(),
        )}
        onSelect={handleSelectItem}
        columns={columns}
      />

      <CartTotal />
    </div>
  );
}
